// PURPOSE: Show the call-first rule beside outreach contact details.
// CONTRACT: Spec 085 B; named and generic email eligibility have distinct hints.
// DEPENDS: NativeRecord and the existing legacy contact projection.
// CEILING: Display only; the server enforces email actions.
import { Mail, Phone } from 'lucide-react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { OutreachInlineLog } from './outreach-inline-log';
import { contactOf } from './outreach-contact';
import type { NativeRecord } from './types';

export function hasDecisionMakerEmail(record: NativeRecord): boolean {
  return record.contacts?.length
    ? record.contacts.some(person => ['decision_maker', '', undefined, null].includes(person.data.contact_role as string | undefined | null) && String(person.data.email || '').trim())
    : Boolean(contactOf(record).email.trim());
}

export function hasAnyEmail(record: NativeRecord): boolean {
  return hasDecisionMakerEmail(record) || Boolean(String(record.data.general_email || '').trim())
    || Boolean(record.contacts?.some(person => person.data.contact_role === 'general' && String(person.data.email || '').trim()));
}

export function CallOnlyHint({ record }: { record: NativeRecord }) {
  const routed = useInRouterContext();
  const hint = !hasDecisionMakerEmail(record) && ['accepted', 'in_cadence'].includes(String(record.data.outreach_state));
  return <><PhoneContactActions record={record} />{hint && <p className={hasAnyEmail(record) ? "small muted" : "small outreach-warning"}>{hasAnyEmail(record) ? "Emails go to the company inbox · call to find the right person" : "No email on file · call first"}</p>}{routed && <RowInlineLog record={record} />}</>;
}

// Spec 077 K1: CSS exposes these links only on the phone outreach rows.
export function PhoneContactActions({ record }: { record: NativeRecord }) {
  const contact = contactOf(record); const company = String(record.data.name || 'Unnamed company');
  const phone = (contact.main || contact.phone || '').trim(); const email = (contact.email || contact.general || '').trim();
  return <div className="outreach-phone-actions">
    {phone ? <a className="icon-button" href={`tel:${phone}`} aria-label={`Call ${company}`}><Phone size={18} aria-hidden="true" /></a> : <button className="icon-button" disabled title="No phone number on file" aria-label={`Call ${company}`}><Phone size={18} aria-hidden="true" /></button>}
    {email ? <a className="icon-button" href={`mailto:${email}`} aria-label={`Email ${company}`}><Mail size={18} aria-hidden="true" /></a> : <button className="icon-button" disabled title="No email address on file" aria-label={`Email ${company}`}><Mail size={18} aria-hidden="true" /></button>}
  </div>;
}

// Keep inline controls on the two work lists; other contact surfaces stay read-only.
function RowInlineLog({ record }: { record: NativeRecord }) {
  const location = useLocation(); const tab = new URLSearchParams(location.search).get('tab');
  return location.pathname === '/outreach' && ['calling', 'followups'].includes(tab || '') ? <OutreachInlineLog record={record} /> : null;
}
